import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { SelectFormField, SwitchFormField } from '@/components/form'
import {
  FieldBooleanBadge,
  FieldRow,
  FieldsTable,
  FieldValue,
  FieldViewValue,
} from '@/components/layout/field-row'
import { TabActions, TabCard } from '@/components/layout/tab-card'
import { Chip } from '@/components/ui/chip'
import { Flex } from '@/components/ui/flex'
import { SelectOption } from '@/components/ui/select'
import { DirtyFieldChange } from '@/hooks'
import { Building } from '@/reducers/types'

import { FormChangeFields, getFormChanges } from '../../../../shared/form-changes'
import { usePlaceSave } from '../../use-place-save'
import {
  getLanguagesDefaults,
  LanguagesFormData,
  makeLanguagesSchema,
  PLACE_FALLBACK_LANGUAGE,
  PLACE_LANGUAGE_LABELS,
  PLACE_LANGUAGES,
  PlaceLanguage,
} from './languages-tab.constants'
import { getLanguagesPayload } from './languages-tab.utils'

interface LanguagesTabProps {
  building: Building
  onDirtyChange: (isDirty: boolean, getChanges?: () => DirtyFieldChange[]) => void
}

const AVAILABLE_OPTIONS: SelectOption<PlaceLanguage>[] = PLACE_LANGUAGES.map((language) => ({
  disabled: language === PLACE_FALLBACK_LANGUAGE,
  label: PLACE_LANGUAGE_LABELS[language],
  value: language,
}))

// Note: every language can be the default - picking one that isn't available is a validation error,
// never a silent change of the other field
const DEFAULT_OPTIONS: SelectOption<PlaceLanguage>[] = PLACE_LANGUAGES.map((language) => ({
  label: PLACE_LANGUAGE_LABELS[language],
  value: language,
}))

const formatLanguages = (value: unknown) =>
  (value as PlaceLanguage[]).map((language) => PLACE_LANGUAGE_LABELS[language])

// Note: Administrator-only tab (place-details.constants.ts) - the backend would accept these fields
// from Managers too, the restriction is a product decision (EL-13870)
export const LanguagesTab = ({ building, onDirtyChange }: LanguagesTabProps) => {
  const { t } = useTranslation(['organisation'])
  const { isSaving, save } = usePlaceSave(building)
  const [isEditing, setIsEditing] = useState(false)

  const schema = useMemo(() => makeLanguagesSchema(t), [t])
  const serverValues = useMemo(() => getLanguagesDefaults(building), [building])

  const form = useForm<LanguagesFormData>({
    defaultValues: serverValues,
    mode: 'onTouched',
    // Note: a background update of the building must not wipe what the user is editing
    resetOptions: { keepDirtyValues: true },
    resolver: zodResolver(schema),
    values: serverValues,
  })

  const { isDirty } = form.formState
  const availableLocale = useWatch({ control: form.control, name: 'availableLocale' })

  // Note: the default is checked against the available list, so a change of the list re-checks a
  // default that already shows an error (onTouched only re-validates the field being changed)
  useEffect(() => {
    if (form.getFieldState('locale').error) form.trigger('locale')
  }, [availableLocale, form])

  const changeFields = useMemo<FormChangeFields<LanguagesFormData>>(
    () => ({
      availableLocale: {
        items: formatLanguages,
        label: t('organisation:places.languages.available.label'),
      },
      i18nAvailable: {
        format: (value) =>
          value ? t('organisation:places.save.on') : t('organisation:places.save.off'),
        label: t('organisation:places.languages.i18nAvailable.label'),
      },
      locale: {
        format: (value) => PLACE_LANGUAGE_LABELS[value as PlaceLanguage] ?? String(value),
        label: t('organisation:places.languages.default.label'),
      },
    }),
    [t],
  )

  const getChanges = useCallback(
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    [changeFields, form],
  )

  useEffect(() => {
    const isDirtyForm = isEditing && isDirty
    onDirtyChange(isDirtyForm, isDirtyForm ? getChanges : undefined)
  }, [isEditing, isDirty, getChanges, onDirtyChange])

  const handleCancel = () => {
    form.reset(serverValues)
    setIsEditing(false)
  }

  // Note: Save is always enabled - handleSubmit validates first and shows every error; a valid form
  // with no changes just leaves edit mode without a request
  const handleSave = form.handleSubmit(async (values) => {
    if (!form.formState.isDirty) {
      setIsEditing(false)
      return
    }

    // Note: JSON, not multipart - `availableLocale` is an array the backend stores as JSON
    const result = await save({
      json: true,
      payload: getLanguagesPayload({ server: building, values }),
    })

    if (result.ok) {
      form.reset(getLanguagesDefaults(result.building))
      setIsEditing(false)
    }
  })

  return (
    <FormProvider {...form}>
      <TabCard
        actions={
          <TabActions
            isEditing={isEditing}
            isSaving={isSaving}
            onCancel={handleCancel}
            onEdit={() => setIsEditing(true)}
            onSave={handleSave}
          />
        }
        title={t('organisation:places.languages.title')}
      >
        <FieldsTable>
          <FieldRow
            hint={t('organisation:places.languages.i18nAvailable.hint')}
            label={t('organisation:places.languages.i18nAvailable.label')}
          >
            {isEditing ? (
              <FieldValue>
                <SwitchFormField name="i18nAvailable" size="small" />
              </FieldValue>
            ) : (
              <FieldViewValue>
                <FieldBooleanBadge value={serverValues.i18nAvailable} />
              </FieldViewValue>
            )}
          </FieldRow>

          <FieldRow
            required={isEditing}
            hint={t('organisation:places.languages.available.hint')}
            label={t('organisation:places.languages.available.label')}
          >
            {isEditing ? (
              <FieldValue>
                <SelectFormField<LanguagesFormData, PlaceLanguage>
                  name="availableLocale"
                  options={AVAILABLE_OPTIONS}
                  placeholder={t('organisation:places.languages.available.placeholder')}
                  size="small"
                  type="multi"
                />
              </FieldValue>
            ) : (
              <FieldViewValue>
                <Flex gap={8} wrap="wrap">
                  {serverValues.availableLocale.map((language) => (
                    <Chip
                      key={language}
                      color="blue"
                      label={PLACE_LANGUAGE_LABELS[language]}
                      size="small"
                      variant="light"
                    />
                  ))}
                </Flex>
              </FieldViewValue>
            )}
          </FieldRow>

          <FieldRow
            required={isEditing}
            hint={t('organisation:places.languages.default.hint')}
            label={t('organisation:places.languages.default.label')}
          >
            {isEditing ? (
              <FieldValue>
                <SelectFormField<LanguagesFormData, PlaceLanguage>
                  name="locale"
                  options={DEFAULT_OPTIONS}
                  placeholder={t('organisation:places.languages.default.placeholder')}
                  size="small"
                  type="single"
                />
              </FieldValue>
            ) : (
              <FieldViewValue>
                {serverValues.locale ? PLACE_LANGUAGE_LABELS[serverValues.locale] : null}
              </FieldViewValue>
            )}
          </FieldRow>
        </FieldsTable>
      </TabCard>
    </FormProvider>
  )
}
